import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ListChecks, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { CompareResult } from "./CompareResult";
import { SheetMissingList } from "./SheetMissingList";

const CheckResult = ({ r }) => (
  <div className="space-y-3 text-sm" data-testid="tabs-check-result">
    <p className="font-semibold text-slate-800">{r.total_rolls} roll numbers found in {r.tabs.length} tabs</p>
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse min-w-full">
        <thead><tr>{["Tab", "Rows", "Roll Nos", "What the app will update"].map((h) => <th key={h} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left">{h}</th>)}</tr></thead>
        <tbody>{r.tabs.map((t) => (
          <tr key={t.tab} data-testid={`tabs-check-row-${t.tab}`}>
            <td className="border border-slate-200 px-2 py-1 font-medium">{t.tab}</td>
            <td className="border border-slate-200 px-2 py-1">{t.rows}</td>
            <td className="border border-slate-200 px-2 py-1">{t.rolls}</td>
            <td className="border border-slate-200 px-2 py-1">{t.lists.map((l, i) => (
              <div key={i}>{[l.paid && "Paid", l.months.length && `months (${l.months[0]}–${l.months[l.months.length - 1]})`].filter(Boolean).join(" + ") || <span className="text-red-600">nothing found</span>}</div>))}</td>
          </tr>))}</tbody>
      </table>
    </div>
    {r.skipped.length > 0 && <p className="text-xs text-slate-500">Not used (no Roll No column): {r.skipped.join(", ")}</p>}
    {r.duplicates.length > 0 && <Warn title="Same roll number in more than one place (these will go to the manual list)" items={r.duplicates.map((d) => `${d.roll}: ${d.places.join(", ")}`)} testid="tabs-dup" />}
    {r.unknown_rolls.length > 0 && <Warn title="Roll numbers not in the app" items={r.unknown_rolls.map((d) => `${d.roll} (${d.place})`)} testid="tabs-unknown" />}
    {r.missing_students.length > 0 && <Warn title={`${r.missing_students.length} app students not in the sheet (their payments go to the manual list)`} items={r.missing_students.map((s) => `${s.roll} ${s.name} (${s.class || "-"})`)} testid="tabs-missing" />}
  </div>
);

const Warn = ({ title, items, testid }) => (
  <details className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid={testid}>
    <summary className="cursor-pointer font-semibold">{title} — {items.length}</summary>
    <ul className="mt-1 list-disc pl-5 max-h-48 overflow-y-auto">{items.map((x) => <li key={x}>{x}</li>)}</ul>
  </details>
);

const ManualList = ({ items, onDone, busy }) => (
  <div className="space-y-2" data-testid="tabs-manual-list">
    <h5 className="font-semibold text-slate-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Update manually ({items.length})</h5>
    {items.length === 0 && <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Nothing to update by hand.</p>}
    {items.map((m) => (
      <div key={m.id} className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs flex items-start gap-3" data-testid={`tabs-manual-${m.id}`}>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">{m.student_name} ({m.roll_number}) — ₹{m.amount} {m.receipt_no && <span className="text-slate-500">· {m.receipt_no}</span>}</div>
          <div className="text-slate-600">{(m.periods || []).filter(Boolean).join(", ")} {m.where && `· ${m.where}`} · {formatDateTime(m.created_at)}</div>
          <div className="text-amber-800">Why: {m.reason}</div>
        </div>
        <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => onDone(m.id)} data-testid={`tabs-manual-done-${m.id}`}>Done</Button>
      </div>))}
  </div>
);

export const SheetTabsPanel = ({ cfg, reload }) => {
  const [check, setCheck] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const loadManual = useCallback(async () => {
    try { const { data } = await api.get("/fees/sheets/manual"); setItems(data.items); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadManual(); }, [loadManual]);
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); } };
  const doCheck = () => run("check", async () => { const { data } = await api.get("/fees/sheets/tabs/check"); setCheck(data); });
  const doCompare = () => run("compare", async () => { const { data } = await api.get("/fees/sheets/tabs/compare"); setCmp(data); });
  const toggle = (v) => run("toggle", async () => { await api.post("/fees/sheets/tabs/enabled", { enabled: v }); toast.success(v ? "Fee tabs will now update automatically" : "Fee tab updates OFF"); await reload(); });
  const done = (id) => run(id, async () => { await api.post(`/fees/sheets/manual/${id}/done`); await loadManual(); });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sheets-step-tabs">
      <div className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-blue-900" /><h4 className="font-semibold text-slate-800">5. Update my fee tabs (month cells & Paid)</h4></div>
      <p className="text-xs text-slate-600">When a payment is final, the app finds the student by <b>Roll No</b> in your tabs. Monthly: writes the amount in that month's cell. Lump-sum: adds to Paid (your Balance formula updates itself). Tabs with both (like 8th) get both. It never overwrites a filled cell — anything unclear goes to the list below.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={doCheck} disabled={!cfg?.connected || busy === "check"} data-testid="tabs-check-btn">{busy === "check" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check sheet (reads only)"}</Button>
        <Button variant="outline" onClick={doCompare} disabled={!cfg?.connected || busy === "compare"} data-testid="tabs-compare-btn">{busy === "compare" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Compare balances: app vs sheet"}</Button>
        <Switch checked={!!cfg?.tabs_enabled} disabled={!cfg?.connected || busy === "toggle"} onCheckedChange={toggle} data-testid="tabs-enable-toggle" />
        <span className="text-sm text-slate-600" data-testid="tabs-enable-status">{cfg?.tabs_enabled ? `ON since ${formatDateTime(cfg.tabs_enabled_at)} — only payments after this are written` : "Off — nothing is written to your tabs"}</span>
      </div>
      {check && <CheckResult r={check} />}
      {cmp && <CompareResult r={cmp} />}
      {cfg?.tabs_enabled && <SheetMissingList />}
      <ManualList items={items} onDone={done} busy={busy} />
    </div>
  );
};
